import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  inject,
  Input,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DecimalPipe, NgIf } from '@angular/common';

@Component({
  selector: 'app-textarea-input',
  templateUrl: './textarea-input.component.html',
  styleUrls: ['./textarea-input.component.scss'],
  standalone: true,
  imports: [NgIf, DecimalPipe],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class TextareaInputComponent implements ControlValueAccessor, AfterViewInit {
  @Input() placeholder: string = '';
  @Input() controlId: string | null = null;
  @Input() error: boolean = false;
  @Input() maxLength: number | null = null;
  @Input() hint: string | null = null;

  @ViewChild('content', { static: false }) content: ElementRef<HTMLDivElement> | undefined;
  @ViewChild('input', { static: false }) input: ElementRef<HTMLInputElement> | undefined;

  private cdr = inject(ChangeDetectorRef);

  public value: string = '';
  public disabled: boolean = false;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public writeValue(value: string | null): void {
    this.value = value === null ? '' : value;
    this.updateValue();
  }

  private updateValue(): void {
    if (!!this.content && !!this.input) {
      this.content.nativeElement.innerText = this.value;
      this.input.nativeElement.value = this.value;
      this.cdr.detectChanges();
    }
  }

  public handleInput(event: Event): void {
    let inputValue = this.content?.nativeElement.innerText ?? '';
    if (inputValue === '\n') inputValue = '';
    if (this.maxLength !== null && inputValue.length > this.maxLength) {
      inputValue = inputValue.slice(0, this.maxLength);
      this.rewriteContent(inputValue);
    }
    this.value = inputValue;
    this.onChange(inputValue);
  }

  // Overwriting innerText discards the caret, so put it back at the end of the text the user is
  // left with — otherwise every keystroke past the limit sends the caret to the start.
  private rewriteContent(value: string): void {
    const content = this.content;
    if (!content) return;

    content.nativeElement.innerText = value;

    const range = document.createRange();
    range.selectNodeContents(content.nativeElement);
    range.collapse(false);

    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  public handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event?.clipboardData?.getData('text/plain');
    document.execCommand('insertText', false, text);
  }

  public handleBlur(): void {
    this.onTouched();
  }

  public ngAfterViewInit(): void {
    this.updateValue();
  }
}
